export interface TelemetryBackendConfig {
  type: 'jaeger' | 'otlp' | 'grafana' | 'honeycomb' | 'datadog' | 'newrelic' | 'console' | 'disabled';
  endpoint?: string;
  apiKey?: string;
  headers?: Record<string, string>;
  dataset?: string; // For Honeycomb
}

export interface TelemetryConfig {
  enabled: boolean;
  serviceName: string;
  serviceVersion: string;
  environment: string;
  sampleRate: number;
  enableConsoleExporter: boolean;
  enableAutoInstrumentation: boolean;
  backend: TelemetryBackendConfig;
}

/**
 * Get telemetry configuration from environment variables
 */
export const getTelemetryConfig = (): TelemetryConfig => {
  const enabled = import.meta.env.VITE_TELEMETRY_ENABLED === 'true';
  const backendType = import.meta.env.VITE_TELEMETRY_BACKEND as TelemetryBackendConfig['type'] || 'console';
  
  // Parse additional headers if provided
  let headers: Record<string, string> = {};
  try {
    if (import.meta.env.VITE_TELEMETRY_HEADERS) {
      headers = JSON.parse(import.meta.env.VITE_TELEMETRY_HEADERS);
    }
  } catch (error) {
    // Note: We can't import logger here due to circular dependency,
    // so we keep the console.warn for configuration parsing errors
    console.warn('Failed to parse VITE_TELEMETRY_HEADERS:', error);
  }

  // Add API key to headers if provided
  const apiKey = import.meta.env.VITE_TELEMETRY_API_KEY;
  if (apiKey) {
    switch (backendType) {
      case 'honeycomb':
        headers['X-Honeycomb-Team'] = apiKey;
        break;
      case 'datadog':
        headers['DD-API-KEY'] = apiKey;
        break;
      case 'newrelic':
        headers['Api-Key'] = apiKey;
        break;
      case 'grafana':
        headers['Authorization'] = `Bearer ${apiKey}`;
        break;
      default:
        headers['Authorization'] = `Bearer ${apiKey}`;
    }
  }

  const backend: TelemetryBackendConfig = {
    type: backendType,
    endpoint: import.meta.env.VITE_TELEMETRY_ENDPOINT,
    apiKey,
    headers,
    dataset: import.meta.env.VITE_TELEMETRY_DATASET,
  };

  return {
    enabled,
    serviceName: import.meta.env.VITE_TELEMETRY_SERVICE_NAME || 'hop-studio-frontend',
    serviceVersion: import.meta.env.VITE_TELEMETRY_SERVICE_VERSION || '1.0.0',
    environment: import.meta.env.VITE_TELEMETRY_ENVIRONMENT || import.meta.env.MODE || 'development',
    sampleRate: parseFloat(import.meta.env.VITE_TELEMETRY_SAMPLE_RATE) || 1.0,
    enableConsoleExporter: import.meta.env.VITE_TELEMETRY_CONSOLE_EXPORT === 'true' || 
                          import.meta.env.DEV || 
                          backendType === 'console',
    enableAutoInstrumentation: import.meta.env.VITE_TELEMETRY_AUTO_INSTRUMENTATION !== 'false',
    backend,
  };
};

/**
 * Get backend-specific configuration examples
 */
export const getBackendExamples = (): Record<string, Partial<TelemetryBackendConfig>> => ({
  jaeger: {
    type: 'jaeger',
    endpoint: 'http://localhost:14268/api/traces',
  },
  otlp: {
    type: 'otlp',
    endpoint: 'http://localhost:4318/v1/traces',
  },
  grafana: {
    type: 'grafana',
    endpoint: 'https://tempo-prod-04-prod-us-central-0.grafana.net:443/tempo/api/traces',
    apiKey: 'your-grafana-api-key',
  },
  honeycomb: {
    type: 'honeycomb',
    endpoint: 'https://api.honeycomb.io/v1/traces',
    apiKey: 'your-honeycomb-api-key',
    dataset: 'your-dataset-name',
  },
  datadog: {
    type: 'datadog',
    endpoint: 'https://trace-agent.datadoghq.com/v0.4/traces',
    apiKey: 'your-datadog-api-key',
  },
  newrelic: {
    type: 'newrelic',
    endpoint: 'https://trace-api.newrelic.com/trace/v1',
    apiKey: 'your-newrelic-license-key',
  },
  console: {
    type: 'console',
    // No endpoint needed - outputs to browser console
  },
  disabled: {
    type: 'disabled',
    // Telemetry completely disabled
  },
});

/**
 * Validate telemetry configuration
 */
export const validateTelemetryConfig = (config: TelemetryConfig): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (!config.serviceName.trim()) {
    errors.push('Service name is required');
  }

  if (!config.serviceVersion.trim()) {
    errors.push('Service version is required');
  }

  if (config.sampleRate < 0 || config.sampleRate > 1) {
    errors.push('Sample rate must be between 0.0 and 1.0');
  }

  if (config.backend.type !== 'console' && config.backend.type !== 'disabled') {
    if (!config.backend.endpoint) {
      errors.push(`Endpoint is required for backend type: ${config.backend.type}`);
    }

    // Validate specific backend requirements
    switch (config.backend.type) {
      case 'honeycomb':
        if (!config.backend.apiKey) {
          errors.push('API key is required for Honeycomb');
        }
        break;
      case 'datadog':
        if (!config.backend.apiKey) {
          errors.push('API key is required for Datadog');
        }
        break;
      case 'newrelic':
        if (!config.backend.apiKey) {
          errors.push('License key is required for New Relic');
        }
        break;
      case 'grafana':
        if (!config.backend.apiKey) {
          errors.push('API key/token is required for Grafana Cloud');
        }
        break;
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}; 